// ============================================================================
// GUEST HOOKS
// ============================================================================
// React hooks for Guest API operations

import { useState, useEffect, useCallback } from 'react';
import { guestService } from 'api/services/guestService';
import type {
  Guest,
  CreateGuestRequest,
  UpdateGuestRequest,
  GuestSearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all guests (paginated)
 */
export const useGuests = (params?: GuestSearchRequest) => {
  const [data, setData] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchGuests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await guestService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchGuests,
  };
};

/**
 * Hook to fetch a single guest by ID
 */
export const useGuest = (id: ID) => {
  const [data, setData] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchGuest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await guestService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGuest();
  }, [fetchGuest]);

  return {
    data,
    loading,
    error,
    refetch: fetchGuest,
  };
};

/**
 * Hook to create a new guest
 */
export const useCreateGuest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createGuest = async (
    data: CreateGuestRequest,
    options?: {
      onSuccess?: (guest: Guest) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await guestService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createGuest,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a guest
 */
export const useUpdateGuest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateGuest = async (
    id: ID,
    data: UpdateGuestRequest,
    options?: {
      onSuccess?: (guest: Guest) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await guestService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateGuest,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a guest
 */
export const useDeleteGuest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteGuest = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await guestService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteGuest,
    loading,
    error,
    success,
  };
};
